/* eslint-disable react/no-unstable-nested-components */
import type { IFinancialsTemplateFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/FinancialsTemplateFieldsFragment.generated";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import type { FinancialsTab } from "components/financials/modern/props";
import type { SelectedCompanyIssuer } from "components/issuers/IssuerByTickerSymbolSearch";
import type { ReactElement } from "react";
import { useState, startTransition } from "react";
import type { IIndicatorDataSource } from "types/graphqlTypes";
import { IFinancialsStatementType } from "types/graphqlTypes";

interface FinancialsStateProps {
  tab: FinancialsTab;
  indicatorDataSource: IIndicatorDataSource;
  statementType?: IFinancialsStatementType;
  interim?: string | null;
  currency?: string;
  reportDates?: [Date | null, Date | null];
  templateId?: string | null;
  indicators?: IIndicatorFieldsFragment[];
  adversaries?: SelectedCompanyIssuer[];
  limit?: number;

  onChangeIndicatorDataSource: (dataSource: IIndicatorDataSource) => void;
  onChangeStatementType: (statementType: IFinancialsStatementType) => void;
  onChangeInterim: (interim: string | null) => void;
  onChangeCurrency: (currency: string) => void;
  onChangeReportDates: (reportDates: [Date | null, Date | null]) => void;
  onChangeTemplate: (template: IFinancialsTemplateFieldsFragment | null) => void;
  onChangeTab: (tab: FinancialsTab) => void;
  onChangeIndicators: (indicators: IIndicatorFieldsFragment[]) => void;
  onChangeAdversaries: (adversaries: SelectedCompanyIssuer[]) => void;
  onChangeLimit: (limit: number) => void;
}

interface LocalFinancialsStateProps {
  indicatorDataSource: IIndicatorDataSource;
  statementType?: IFinancialsStatementType;
  currency?: string;
  children: (state: FinancialsStateProps) => ReactElement;
}

export default function LocalFinancialsState({
  indicatorDataSource,
  statementType = IFinancialsStatementType.Annual,
  currency = "USD",
  children,
}: LocalFinancialsStateProps) {
  const [tab, setTab] = useState<FinancialsTab>("TABLE");
  const [_indicatorDataSource, setIndicatorDataSource] = useState<IIndicatorDataSource>(indicatorDataSource);
  const [_statementType, setStatementType] = useState<IFinancialsStatementType>(statementType);
  const [interim, setInterim] = useState<string | null>(null);
  const [reportDates, setReportDates] = useState<[Date | null, Date | null]>([null, null]);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [indicators, setIndicators] = useState<IIndicatorFieldsFragment[]>([]);
  const [adversaries, setAdversaries] = useState<SelectedCompanyIssuer[]>([]);
  const [limit, setLimit] = useState<number>(5);
  const [_currency, setCurrency] = useState<string>(currency);

  return children({
    tab,
    indicatorDataSource: _indicatorDataSource,
    statementType: _statementType,
    interim,
    currency: _currency,
    reportDates,
    templateId,
    indicators,
    adversaries,
    limit,

    onChangeTab: setTab,
    onChangeIndicatorDataSource: (dataSource) => {
      startTransition(() => {
        setReportDates([null, null]);
        setIndicatorDataSource(dataSource);
      });
    },
    onChangeStatementType: setStatementType,
    onChangeInterim: setInterim,
    onChangeCurrency: setCurrency,
    onChangeReportDates: setReportDates,
    onChangeTemplate: (template) => setTemplateId(template?.objectId || null),
    onChangeIndicators: setIndicators,
    onChangeAdversaries: setAdversaries,
    onChangeLimit: setLimit,
  });
}
